import React, { Component, CSSProperties, Fragment } from 'react';
import { PageContent } from '../component/pageContent';
import { registryEditorStore } from '../store/registry-editor-store';
import { observer } from 'mobx-react';
import { registryEditorApi } from '../api/registry-editor';
import styled from 'styled-components';
import { InputWithConfirm, Input } from '../component/input';
import { theme } from '../styles/theme';
import { Select } from '../component/select';
import { RegistrySearchFlags } from '../enums';
import { TreeView } from '../component/treeView';
import {
  faFolder,
  faListOl,
  faFont,
  faAngleDown,
} from '@fortawesome/pro-regular-svg-icons';
import { Loader } from '../component/loader';
import { PageContentHeader } from '../component/pageContent';
import { DataTable } from '../component/table/dataTable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ButtonGroup, Button } from '../component/button';
import ReactTooltip from 'react-tooltip';
import { Dropdown, PromptDialog } from '../component';
import { messageActions } from '../action';
import { TextArea } from '../component/input/textarea';

const LargePageBreak = 1000;
const SmallPageBreak = 800;

const ControlsGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  grid-column-gap: 10px;
  grid-row-gap: 10px;
  margin-bottom: 20px;
  align-items: start;

  @media screen and (max-width: ${LargePageBreak}px) {
    grid-template-columns: 1fr 1fr;
  }

  @media screen and (max-width: ${SmallPageBreak}px) {
    grid-template-columns: 1fr;
  }
`;

const ControlGroupLabel = styled.div`
  margin-top: 10px;
  margin-left: 10px;
  color: gray;
  font-size: 0.8em;
`;

const ContentPane = styled.div`
  display: grid;
  grid-template-columns: auto auto 2fr;
`;

const Divider = styled.div`
  height: 100%;
  width: 5px;
  background: lightgray;
  border: 1px solid gray;
  border-radius: 5px;
  margin-left: 5px;
  margin-right: 5px;
  cursor: ew-resize;
  opacity: 0.5;
  transition: 0.2s opacity linear;

  &:hover {
    opacity: 1;
    transition: 0.2s opacity linear;
  }
`;

const SplitGridWithHeader = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  height: 100%;
`;

interface RegistryEditorProps {}

interface RegistryEditorState {
  isSetKeyPromptOpen?: boolean;
  isSetValuePromptOpen?: boolean;
  setKeyOperation?: 'New' | 'Rename';
  setValueKind?: RegistryValueKind;
  setValueOperation?: 'New' | 'Rename' | 'Modify';
}

@observer
class RegistryEditorComp extends Component<
  RegistryEditorProps,
  RegistryEditorState
> {
  constructor(props: RegistryEditorProps) {
    super(props);
    this.treeViewDiv = React.createRef();
    this.state = {
      isSetKeyPromptOpen: false,
      isSetValuePromptOpen: false,
    };
  }

  treeViewDiv: React.RefObject<HTMLDivElement>;
  isDividerDragging: boolean;
  startDividerDragTreeWidth: number;
  startDividerDragPageX: number;

  componentDidMount() {
    window.addEventListener('keyup', this.onKeyUp);
    if ((registryEditorStore.rootKeys || []).length == 0) {
      registryEditorApi.getRootKeys();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('keyup', this.onKeyUp);
  }

  onKeyUp(ev: KeyboardEvent) {
    if (
      ev.key.toLowerCase() == 'escape' &&
      !registryEditorStore.isSearchPaneHidden
    ) {
      if (registryEditorStore.searchStatus == 'In Progress') {
        registryEditorStore.setSearchStatus('Cancelling');
        registryEditorApi.cancelSearch();
      }
      registryEditorStore.setIsSearchPaneHidden(true);
    }
  }

  render() {
    return (
      <div style={{ height: '100%' }}>
        <SplitGridWithHeader
          onMouseUp={() => {
            this.isDividerDragging = false;
          }}
          onMouseLeave={() => {
            this.isDividerDragging = false;
          }}
          onMouseMove={ev => {
            if (this.isDividerDragging) {
              ev.preventDefault();
              ev.stopPropagation();
              var newWidth =
                this.startDividerDragTreeWidth +
                ev.pageX -
                this.startDividerDragPageX;
              this.treeViewDiv.current.style.width = String(newWidth) + 'px';
            }
          }}
        >
          {this.renderControlsGrid()}

          {this.renderContentPane()}
        </SplitGridWithHeader>

        {this.renderModals()}

        {registryEditorStore.isLoading && (
          <Loader text="Loading..." style={{ zIndex: 3 }} />
        )}
      </div>
    );
  }

  renderActionButtonsGroup() {
    return (
      <ButtonGroup style={{ flexWrap: 'wrap' }}>
        <Dropdown
          text={
            <div>
              <Button
                content="New"
                data-tip
                data-for="new-tooltip"
                disabled={!registryEditorStore.selectedNode}
                faIconProps={{ icon: faAngleDown }}
                style={{ height: '100%', marginTop: '5px' }}
              />

              <ReactTooltip
                id="new-tooltip"
                place="top"
                effect="solid"
                type="dark"
              >
                <span> New&nbsp;Item </span>
              </ReactTooltip>
            </div>
          }
        >
          <Button
            onClick={() => {
              registryEditorStore.setInputItemName('');
              registryEditorStore.setInputItemData('');

              this.setState({
                isSetKeyPromptOpen: true,
                setKeyOperation: 'New',
              });
            }}
            data-tip
            data-for="newkey-tooltip"
            size="small"
            aria-label="New key"
            disabled={!registryEditorStore.selectedNode}
            style={{ display: 'block', width: '100%' }}
            content="Key"
          />
          <ReactTooltip
            id="newkey-tooltip"
            place="top"
            effect="solid"
            type="dark"
          >
            <span> New&nbsp;Key </span>
          </ReactTooltip>

          <Button
            onClick={() => {
              registryEditorStore.setInputItemName('');
              registryEditorStore.setInputItemData('');

              this.setState({
                isSetValuePromptOpen: true,
                setValueKind: 'String',
                setValueOperation: 'New',
              });
            }}
            data-tip
            data-for="newstringvalue-tooltip"
            size="small"
            aria-label="New string value"
            disabled={!registryEditorStore.selectedNode}
            style={{ display: 'block', width: '100%' }}
            content="String Value"
          />
          <ReactTooltip
            id="newstringvalue-tooltip"
            place="top"
            effect="solid"
            type="dark"
          >
            <span> New&nbsp;String&nbsp;Value </span>
          </ReactTooltip>

          <Button
            onClick={() => {
              registryEditorStore.setInputItemName('');
              registryEditorStore.setInputItemData('');

              this.setState({
                isSetValuePromptOpen: true,
                setValueKind: 'DWord',
                setValueOperation: 'New',
              });
            }}
            data-tip
            data-for="newdwordvalue-tooltip"
            size="small"
            aria-label="New DWORD value"
            disabled={!registryEditorStore.selectedNode}
            style={{ display: 'block', width: '100%' }}
            content="DWORD (32-bit) Value"
          />
          <ReactTooltip
            id="newdwordvalue-tooltip"
            place="top"
            effect="solid"
            type="dark"
          >
            <span> New&nbsp;DWORD&nbsp;Value </span>
          </ReactTooltip>

          <Button
            onClick={() => {
              registryEditorStore.setInputItemName('');
              registryEditorStore.setInputItemData('');

              this.setState({
                isSetValuePromptOpen: true,
                setValueKind: 'QWord',
                setValueOperation: 'New',
              });
            }}
            data-tip
            data-for="newqwordvalue-tooltip"
            size="small"
            aria-label="New QWORD value"
            disabled={!registryEditorStore.selectedNode}
            style={{ display: 'block', width: '100%' }}
            content="QWORD (64-bit) Value"
          />
          <ReactTooltip
            id="newqwordvalue-tooltip"
            place="top"
            effect="solid"
            type="dark"
          >
            <span> New&nbsp;QWORD&nbsp;Value </span>
          </ReactTooltip>

          <Button
            onClick={() => {
              registryEditorStore.setInputItemName('');
              registryEditorStore.setInputItemData('');

              this.setState({
                isSetValuePromptOpen: true,
                setValueKind: 'MultiString',
                setValueOperation: 'New',
              });
            }}
            data-tip
            data-for="newmultistringvalue-tooltip"
            size="small"
            aria-label="New multi-string value"
            disabled={!registryEditorStore.selectedNode}
            style={{ display: 'block', width: '100%' }}
            content="Multi-String Value"
          />
          <ReactTooltip
            id="newmultistringvalue-tooltip"
            place="top"
            effect="solid"
            type="dark"
          >
            <span> New&nbsp;Multi-String&nbsp;Value </span>
          </ReactTooltip>

          <Button
            onClick={() => {
              registryEditorStore.setInputItemName('');
              registryEditorStore.setInputItemData('');

              this.setState({
                isSetValuePromptOpen: true,
                setValueKind: 'ExpandString',
                setValueOperation: 'New',
              });
            }}
            data-tip
            data-for="newexpandablestringvalue-tooltip"
            size="small"
            aria-label="New expandable string value"
            disabled={!registryEditorStore.selectedNode}
            style={{ display: 'block', width: '100%' }}
            content="Expandable String Value"
          />
          <ReactTooltip
            id="newexpandablestringvalue-tooltip"
            place="top"
            effect="solid"
            type="dark"
          >
            <span> New&nbsp;Expandable&nbsp;String&nbsp;Value </span>
          </ReactTooltip>
        </Dropdown>

        <Dropdown
          text={
            <div>
              <Button
                content="Rename"
                data-tip
                data-for="renameitem-tooltip"
                faIconProps={{ icon: faAngleDown }}
                disabled={!registryEditorStore.selectedNode}
                style={{ marginLeft: '5px', marginTop: '5px' }}
              />

              <ReactTooltip
                id="renameitem-tooltip"
                place="top"
                effect="solid"
                type="dark"
              >
                <span> Rename&nbsp;Item </span>
              </ReactTooltip>
            </div>
          }
        >
          <div style={{ minWidth: '100px' }}>
            <Button
              onClick={() => {
                registryEditorStore.setInputItemName(
                  registryEditorStore.selectedNode.displayName,
                );
                this.setState({
                  isSetKeyPromptOpen: true,
                  setKeyOperation: 'Rename',
                });
              }}
              data-tip
              data-for="renamekey-tooltip"
              size="small"
              aria-label="Rename key"
              disabled={!registryEditorStore.selectedNode}
              style={{ display: 'block', width: '100%' }}
              content="Key"
            />
            <ReactTooltip
              id="renamekey-tooltip"
              place="top"
              effect="solid"
              type="dark"
            >
              <span> Rename&nbsp;Key </span>
            </ReactTooltip>

            <Button
              onClick={() => {
                registryEditorStore.setInputItemName(
                  registryEditorStore.selectedValue.name,
                );
                registryEditorStore.setInputItemData(
                  registryEditorStore.selectedValue.value,
                );

                this.setState({
                  isSetValuePromptOpen: true,
                  setValueOperation: 'Rename',
                  setValueKind: registryEditorStore.selectedValue.kind,
                });
              }}
              data-tip
              data-for="renamevalue-tooltip"
              size="small"
              aria-label="Rename value"
              disabled={!registryEditorStore.selectedValue}
              style={{ display: 'block', width: '100%' }}
              content="Value"
            />
            <ReactTooltip
              id="renamevalue-tooltip"
              place="top"
              effect="solid"
              type="dark"
            >
              <span> Rename&nbsp;Value </span>
            </ReactTooltip>
          </div>
        </Dropdown>

        <Dropdown
          text={
            <div>
              <Button
                content="Delete"
                data-tip
                data-for="deleteitem-tooltip"
                faIconProps={{ icon: faAngleDown }}
                disabled={!registryEditorStore.selectedNode}
                style={{ marginLeft: '5px', marginTop: '5px' }}
              />

              <ReactTooltip
                id="deleteitem-tooltip"
                place="top"
                effect="solid"
                type="dark"
              >
                <span> Delete&nbsp;Item </span>
              </ReactTooltip>
            </div>
          }
        >
          <div style={{ minWidth: '100px' }}>
            <Button
              onClick={() => {
                if (
                  !registryEditorStore.selectedNode.fullKeyName.includes('\\')
                ) {
                  messageActions.msg('warning', 'Cannot delete root key.');
                  return;
                }
                var currentKey = registryEditorStore.selectedNode.displayName;
                var result = confirm(
                  `Are you sure you want to delete "${currentKey}" key and all its subkeys?`,
                );
                if (result) {
                  registryEditorApi.deleteKey(registryEditorStore.selectedNode);
                }
              }}
              data-tip
              data-for="deletekey-tooltip"
              size="small"
              aria-label="Delete key"
              disabled={!registryEditorStore.selectedNode}
              style={{ display: 'block', width: '100%' }}
              content="Key"
            />
            <ReactTooltip
              id="deletekey-tooltip"
              place="top"
              effect="solid"
              type="dark"
            >
              <span> Delete&nbsp;Key </span>
            </ReactTooltip>

            <Button
              onClick={() => {
                var result = confirm(
                  `Are you sure you want to delete "${
                    registryEditorStore.selectedValue.name
                  }" value?`,
                );
                if (result) {
                  registryEditorApi.deleteValue(
                    registryEditorStore.selectedValue,
                  );
                }
              }}
              data-tip
              data-for="deletevalue-tooltip"
              size="small"
              aria-label="Delete value"
              disabled={!registryEditorStore.selectedValue}
              style={{ display: 'block', width: '100%' }}
              content="Value"
            />
            <ReactTooltip
              id="deletevalue-tooltip"
              place="top"
              effect="solid"
              type="dark"
            >
              <span> Delete&nbsp;Value </span>
            </ReactTooltip>
          </div>
        </Dropdown>

        <Button
          onClick={() => {
            if (
              registryEditorStore.selectedValue.kind == 'None' ||
              registryEditorStore.selectedValue.kind == 'Unknown'
            ) {
              messageActions.msg(
                'warning',
                'Unable to modify this value type.',
              );
              return;
            }
            if (registryEditorStore.selectedValue.kind == 'Binary') {
              messageActions.msg(
                'warning',
                'Modifying binary data is unsupported.',
              );
              return;
            }
            registryEditorStore.setInputItemName(
              registryEditorStore.selectedValue.name,
            );
            registryEditorStore.setInputItemData(
              registryEditorStore.selectedValue.value,
            );

            this.setState({
              isSetValuePromptOpen: true,
              setValueOperation: 'Modify',
              setValueKind: registryEditorStore.selectedValue.kind,
            });
          }}
          data-tip
          data-for="modifyvalue-tooltip"
          size="small"
          aria-label="Modify value"
          disabled={!registryEditorStore.selectedValue}
          style={{ marginLeft: '5px', marginTop: '5px' }}
          content="Modify Value"
        />
        <ReactTooltip
          id="modifyvalue-tooltip"
          place="top"
          effect="solid"
          type="dark"
        >
          <span> Modify&nbsp;Value </span>
        </ReactTooltip>

        {this.renderModals()}
      </ButtonGroup>
    );
  }

  renderContentPane() {
    if ((registryEditorStore.rootKeys || []).length == 0) {
      return <Loader text="Loading registry data" />;
    }

    return (
      <ContentPane>
        <div
          ref={this.treeViewDiv}
          style={{ overflow: 'auto', width: '250px' }}
        >
          <KeyTree />
        </div>

        <div>
          <Divider
            onMouseDown={ev => {
              this.isDividerDragging = true;
              this.startDividerDragTreeWidth = this.treeViewDiv.current.clientWidth;
              this.startDividerDragPageX = ev.pageX;
            }}
          />
        </div>

        <div style={{ overflow: 'auto' }}>
          <ValuesTable />
        </div>
      </ContentPane>
    );
  }

  renderControlsGrid() {
    const searchFlags = Object.keys(RegistrySearchFlags)
      .filter(x => isNaN(Number(x)) && RegistrySearchFlags[x] > 0)
      .map(x => ({
        value: RegistrySearchFlags[x],
        label:
          RegistrySearchFlags[x] == RegistrySearchFlags.WholeStringOnly
            ? 'Match whole string only'
            : x,
      }));

    const selectedSearchFlags = searchFlags.filter(
      x => x.value > 0 && registryEditorStore.hasSearchFlags(x.value),
    );

    return (
      <ControlsGrid>
        <div>
          <Input
            value={registryEditorStore.currentAddressText}
            onChange={(ev, val) => {
              registryEditorStore.setAddressText(val);
            }}
            onBlur={() => {
              if (registryEditorStore.selectedNode) {
                registryEditorStore.setAddressText(
                  registryEditorStore.selectedNode.fullKeyName,
                );
              }
            }}
            onKeyUp={ev => {
              if (ev.key.toLowerCase() == 'enter') {
                ev.persist();

                if (!registryEditorStore.currentAddressText) {
                  return;
                }

                registryEditorApi
                  .getKeyTree(registryEditorStore.currentAddressText)
                  .then(() => {
                    registryEditorStore.scrollToNode(
                      registryEditorStore.currentAddressText,
                    );
                  })
                  .catch(() => {});
              }
            }}
            placeholder="Enter or navigate to registry location."
          />

          <div>
            <ControlGroupLabel>Actions</ControlGroupLabel>

            {this.renderActionButtonsGroup()}
          </div>
        </div>

        <div>
          <InputWithConfirm
            value={String(registryEditorStore.searchString)}
            onChange={(ev, val) => {
              registryEditorStore.setSearchString(val);
            }}
            onConfirm={() => {
              if (!registryEditorStore.selectedNode) {
                messageActions.msg('warning', 'Select a registry key first.');
                return;
              }

              if (
                registryEditorStore.searchStatus == 'In Progress' ||
                registryEditorStore.searchStatus == 'Cancelling'
              ) {
                messageActions.msg('warning', 'Search already in progress.');
                return;
              }

              registryEditorStore.clearSearchMatches();
              registryEditorStore.setSearchStatus('In Progress');

              registryEditorStore.setIsSearchPaneOpen(true);
              registryEditorStore.setIsSearchPaneHidden(false);
              registryEditorStore.setSearchPaneDisplayPage(1);
              registryEditorStore.setSearchPanePage(1);

              registryEditorApi.searchRegistry(
                registryEditorStore.selectedNode.fullKeyName,
                registryEditorStore.searchString,
                registryEditorStore.searchFlags,
              );
            }}
            onEscape={() => {
              registryEditorStore.setSearchString('');
            }}
            placeholder="Search the registry."
            icon="search"
            iconStyle={{ color: theme.primaryColor }}
          />

          <ControlGroupLabel>Search Options</ControlGroupLabel>

          <Select
            isMulti
            closeMenuOnSelect={false}
            placeholder="Search Options"
            options={searchFlags}
            value={selectedSearchFlags}
            onChange={(ev: Array<{ label: string; value: string }>) => {
              var searchFlags = RegistrySearchFlags.None;
              if (ev) {
                ev.forEach(val => (searchFlags |= parseInt(val.value)));
                registryEditorStore.setSearchFlags(searchFlags);
              } else {
                registryEditorStore.setSearchFlags(searchFlags);
              }
            }}
          />
        </div>
      </ControlsGrid>
    );
  }

  renderModals() {
    var onKeyInputConfirm = () => {
      this.setState({
        isSetKeyPromptOpen: false,
      });

      switch (this.state.setKeyOperation) {
        case 'New':
          registryEditorApi.createNewKey(
            registryEditorStore.selectedNode.fullKeyName,
            registryEditorStore.inputItemName,
          );
          break;
        case 'Rename':
          if (!registryEditorStore.selectedNode.parentKeyNode) {
            messageActions.msg('warning', 'Cannot rename root key.');
            return;
          }
          registryEditorApi.renameRegistryKey(
            registryEditorStore.selectedNode,
            registryEditorStore.inputItemName,
          );
          break;
        default:
          return;
      }
    };

    var onValueInputConfirm = () => {
      this.setState({
        isSetValuePromptOpen: false,
      });

      switch (this.state.setValueOperation) {
        case 'New':
          registryEditorApi.createRegistryValue(
            registryEditorStore.selectedNode.fullKeyName,
            registryEditorStore.inputItemName,
            registryEditorStore.inputItemData,
            this.state.setValueKind,
          );
          break;
        case 'Rename':
          registryEditorApi.renameRegistryValue(
            registryEditorStore.selectedValue.keyName,
            registryEditorStore.selectedValue.name,
            registryEditorStore.inputItemName,
          );
          break;
        case 'Modify':
          registryEditorApi.modifyRegistryValue(
            registryEditorStore.selectedValue.keyName,
            registryEditorStore.selectedValue.name,
            registryEditorStore.inputItemData,
            this.state.setValueKind,
          );
          break;
        default:
          return;
      }
    };
    return (
      <div>
        <PromptDialog
          isOpen={this.state.isSetKeyPromptOpen}
          cancelButtonText="Cancel"
          confirmButtonText="Save"
          onCancel={() => {
            this.setState({
              isSetKeyPromptOpen: false,
            });
          }}
          onConfirm={onKeyInputConfirm}
          title={`${this.state.setKeyOperation} Key`}
          body={
            <InputWithConfirm
              placeholder="Enter key name."
              value={String(registryEditorStore.inputItemName || '')}
              onConfirm={onKeyInputConfirm}
              onChange={(ev, val) => {
                registryEditorStore.setInputItemName(val);
              }}
            />
          }
        />

        <PromptDialog
          isOpen={this.state.isSetValuePromptOpen}
          cancelButtonText="Cancel"
          confirmButtonText="Save"
          onCancel={() => {
            this.setState({
              isSetValuePromptOpen: false,
            });
          }}
          onConfirm={onValueInputConfirm}
          title={`${this.state.setValueOperation} ${
            this.state.setValueKind
          } Value`}
          body={
            <div style={{ width: '100%' }}>
              <InputWithConfirm
                readOnly={this.state.setValueOperation == 'Modify'}
                disabled={this.state.setValueOperation == 'Modify'}
                value={
                  this.state.setValueOperation == 'New'
                    ? String(registryEditorStore.inputItemName || '')
                    : String(registryEditorStore.inputItemName || '(Default)')
                }
                placeholder="Enter name."
                onConfirm={onValueInputConfirm}
                onChange={(ev, val) => {
                  registryEditorStore.setInputItemName(val);
                }}
              />
              {this.state.setValueKind == 'MultiString' ? (
                <TextArea
                  placeholder="Enter data."
                  readOnly={this.state.setValueOperation == 'Rename'}
                  disabled={this.state.setValueOperation == 'Rename'}
                  value={String(registryEditorStore.inputItemData || '')}
                  style={{
                    marginTop: '15px',
                  }}
                  onChange={ev => {
                    registryEditorStore.setInputItemData(ev.target.value);
                  }}
                />
              ) : (
                <InputWithConfirm
                  placeholder="Enter data."
                  readOnly={this.state.setValueOperation == 'Rename'}
                  disabled={this.state.setValueOperation == 'Rename'}
                  value={String(registryEditorStore.inputItemData || '')}
                  style={{ marginTop: '5px' }}
                  type={
                    this.state.setValueKind == 'DWord' ||
                    this.state.setValueKind == 'QWord'
                      ? 'number'
                      : 'text'
                  }
                  onConfirm={onValueInputConfirm}
                  onChange={(ev, val) => {
                    registryEditorStore.setInputItemData(val);
                  }}
                />
              )}
            </div>
          }
        />
      </div>
    );
  }
}

@observer
class KeyTree extends React.Component {
  render() {
    return (
      <TreeView
        data={registryEditorStore.rootKeys}
        iconSelector={() => faFolder}
        onNodeClick={dataItem => {
          registryEditorStore.setSelectedNode(dataItem);
          if (registryEditorStore.isSearchPaneOpen) {
            registryEditorStore.setIsSearchPaneOpen(false);
          }
        }}
        onNodeDblClick={dataItem => {
          registryEditorStore.toggleKeyExpansion(dataItem);
        }}
        onNodeExpandoClick={dataItem => {
          registryEditorStore.toggleKeyExpansion(dataItem);
        }}
        displayNameSelector={item => item.displayName}
        recursiveNodeSelector={item => item.childKeyNodes}
      />
    );
  }
}

@observer
class ValuesTable extends React.Component {
  render() {
    return (
      <DataTable
        data={
          registryEditorStore.selectedNode
            ? registryEditorStore.selectedNode.values
            : []
        }
        dataKey={'name'}
        initialSortField={'name'}
        initialSortOrder="asc"
        rowHoverHighlight
        maxWidth
        divided
        sortable
        rowSelectable
        selectedRowItem={registryEditorStore.selectedValue}
        rowSelectedCallback={item => {
          registryEditorStore.setSelectedValue(item);
          if (registryEditorStore.isSearchPaneOpen) {
            registryEditorStore.setIsSearchPaneOpen(false);
          }
        }}
        columns={[
          {
            header: '',
            key: 'icon',
            render: item => {
              var rowIcon;
              switch (item.kind) {
                case 'Binary':
                case 'DWord':
                case 'QWord':
                  rowIcon = faListOl;
                  break;
                case 'ExpandString':
                case 'MultiString':
                case 'String':
                  rowIcon = faFont;
                  break;
                default:
                  rowIcon = null;
                  break;
              }
              return (
                <Fragment>
                  <FontAwesomeIcon
                    icon={rowIcon}
                    data-tip
                    data-for={item.name + '-tooltip'}
                  />

                  <ReactTooltip
                    id={item.name + '-tooltip'}
                    place="top"
                    effect="solid"
                    type="dark"
                  >
                    <span>{item.kind + ' Value Type'}</span>
                  </ReactTooltip>
                </Fragment>
              );
            },
          },
          {
            header: 'Name',
            sortField: 'name',
            render: x => x.name || '(Default)',
          },
          {
            header: 'Type',
            sortField: 'kind',
            render: x => x.kind,
          },
          {
            header: 'Data',
            sortField: 'value',
            render: x => {
              if (x.kind == 'MultiString') {
                return (x.value as string[]).join('\n');
              }

              return String(x.value);
            },
          },
        ]}
      />
    );
  }
}

@observer
class SearchPane extends React.Component {
  pageSetterTimeout: number;

  onPageChange(ev: React.ChangeEvent<HTMLInputElement>) {
    window.clearTimeout(this.pageSetterTimeout);

    ev.persist();

    var newValue = Number(ev.target.value);

    if (isNaN(newValue)) {
      ev.preventDefault();
      return;
    }

    if (newValue < 1 || newValue > registryEditorStore.searchPaneTotalPages) {
      ev.preventDefault();
      return;
    }

    registryEditorStore.setSearchPaneDisplayPage(newValue);

    this.pageSetterTimeout = window.setTimeout(() => {
      registryEditorStore.setSearchPanePage(newValue);
      ev.target.blur();
    }, 1500);
  }

  renderPaginationControls() {
    if (!registryEditorStore.isSearchPaneOpen) {
      return null;
    }

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button
            icon="arrowLeft"
            data-tip
            data-for="back-tooltip"
            aria-label="Page back"
            size="small"
            onClick={() => {
              if (registryEditorStore.searchPanePage > 1) {
                var newPage = registryEditorStore.searchPanePage - 1;
                registryEditorStore.setSearchPanePage(newPage);
                registryEditorStore.setSearchPaneDisplayPage(newPage);
              }
            }}
          />

          <span
            style={{ color: 'gray', margin: '0 5px 0 5px', fontSize: '.9em' }}
          >
            Page
          </span>

          <ReactTooltip
            id="back-tooltip"
            place="top"
            effect="solid"
            type="dark"
          >
            <span>Back</span>
          </ReactTooltip>

          <Input
            size="small"
            style={{ width: '80px', textAlign: 'center' }}
            value={String(registryEditorStore.searchPaneDisplayPage)}
            onBlur={() => {
              if (
                !isNaN(registryEditorStore.searchPaneDisplayPage) &&
                registryEditorStore.searchPaneDisplayPage > 0 &&
                registryEditorStore.searchPaneDisplayPage <=
                  registryEditorStore.searchPaneTotalPages
              ) {
                registryEditorStore.setSearchPanePage(
                  registryEditorStore.searchPaneDisplayPage,
                );
              }
            }}
            onChange={ev => this.onPageChange(ev)}
          />

          <span
            style={{ color: 'gray', margin: '0 5px 0 5px', fontSize: '.9em' }}
          >
            of {registryEditorStore.searchPaneTotalPages}
          </span>

          <Button
            icon="arrowRight"
            data-tip
            data-for="forward-tooltip"
            aria-label="Page forward"
            size="small"
            onClick={() => {
              if (
                registryEditorStore.searchPanePage <
                registryEditorStore.searchPaneTotalPages
              ) {
                var newPage = registryEditorStore.searchPanePage + 1;
                registryEditorStore.setSearchPanePage(newPage);
                registryEditorStore.setSearchPaneDisplayPage(newPage);
              }
            }}
          />

          <ReactTooltip
            id="forward-tooltip"
            place="top"
            effect="solid"
            type="dark"
          >
            <span>Forward</span>
          </ReactTooltip>
        </div>
      </div>
    );
  }

  renderSearchResultsTable() {
    if (!registryEditorStore.isSearchPaneOpen) {
      return null;
    }

    return (
      <div style={{ overflow: 'auto' }}>
        <DataTable
          maxWidth
          data={registryEditorStore.searchMatchesDisplayed}
          dataKey={item => item.parentKeyPath + item.itemName}
          divided
          rowHoverHighlight
          rowSelectedCallback={async item => {
            var subkey = item.parentKeyPath;

            if (item.kind == 'Key') {
              subkey += `\\${item.itemName}`;
            }

            registryEditorStore.setIsLoading(true);

            try {
              await registryEditorApi.getKeyTree(subkey);

              if (item.kind == 'Value') {
                var targetValue = registryEditorStore.selectedNode.values.find(
                  x => x.name == item.itemName,
                );
                if (targetValue) {
                  registryEditorStore.setSelectedValue(targetValue);
                }
              }
            } finally {
              registryEditorStore.setIsLoading(false);
              registryEditorStore.setIsSearchPaneOpen(false);
              registryEditorStore.scrollToNode(subkey);
            }
          }}
          columns={[
            {
              header: 'Kind',
              render: item => item.kind,
            },
            {
              header: 'Name',
              render: item => item.itemName,
            },
            {
              header: 'Parent Key',
              render: item => item.parentKeyPath,
            },
          ]}
        />
      </div>
    );
  }

  render() {
    var fontWeight = 'unset';
    var fontColor = 'unset';

    const outerDivCss = {
      display: 'grid',
      gridTemplateRows: 'auto 1fr',
      gridRowGap: '5px',
      right: '30px',
      bottom: 0,
      height: registryEditorStore.isSearchPaneHidden
        ? '0'
        : registryEditorStore.isSearchPaneOpen
          ? '450px'
          : '75px',
      maxHeight: '90%',
      width: registryEditorStore.isSearchPaneOpen
        ? 'calc(100% - 290px)'
        : '50%',
      minWidth: '450px',
      padding: '20px',
      position: 'fixed',
      zIndex: 2,
      overflowY: registryEditorStore.isSearchPaneOpen ? 'auto' : 'hidden',
      border: '2px solid darkgray',
      boxShadow:
        '2px 2px 10px 0 rgba(0, 0, 0, 0.1), 2px 2px 10px 0 rgba(101, 69, 167, 0.3)',
      backgroundColor: 'whitesmoke',
      transitionProperty: 'width, height',
      transitionDuration: '.25s',
      transitionTimingFunction: 'linear',
    } as CSSProperties;

    if (registryEditorStore.isSearchPaneHidden) {
      outerDivCss.transform = 'scaleY(0)';
    }

    switch (registryEditorStore.searchStatus) {
      case 'Completed':
        fontWeight = 'bold';
        fontColor = theme.primaryColor;
        break;
      case 'Cancelled':
        fontWeight = 'bold';
        fontColor = 'darkorange';
        break;
      case 'Error':
        fontWeight = 'bold';
        fontColor = 'red';
        break;
      case 'In Progress':
      case 'Cancelled':
      case 'Cancelling':
      default:
        fontWeight = 'unset';
        fontColor = 'unset';
        break;
    }
    const searchStatusCss = {
      fontWeight: fontWeight,
      color: fontColor,
      marginLeft: '5px',
    } as CSSProperties;

    return (
      <div style={outerDivCss}>
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr' }}>
          <h3
            style={{ alignSelf: 'start', marginTop: 0, whiteSpace: 'nowrap' }}
          >
            Search Results
            <small style={{ marginLeft: '10px' }}>
              (Status:
              <span style={searchStatusCss}>
                {registryEditorStore.searchStatus}
              </span>
              {registryEditorStore.searchStatus == 'In Progress' && (
                <a
                  style={{
                    marginLeft: '10px',
                    cursor: 'pointer',
                    fontSize: '.8em',
                  }}
                  onClick={() => {
                    registryEditorStore.setSearchStatus('Cancelling');
                    registryEditorApi.cancelSearch();
                  }}
                >
                  Cancel
                </a>
              )})
            </small>
          </h3>

          <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
            <Button
              primary
              icon={
                registryEditorStore.isSearchPaneOpen ? 'minimize2' : 'maximize2'
              }
              style={{ marginRight: '15px' }}
              onClick={() => {
                registryEditorStore.setIsSearchPaneOpen(
                  !registryEditorStore.isSearchPaneOpen,
                );
              }}
            />
            <Button
              primary
              icon="x"
              onClick={() => {
                registryEditorApi.cancelSearch();
                registryEditorStore.clearSearchMatches();
                registryEditorStore.setIsSearchPaneOpen(false);
                registryEditorStore.setIsSearchPaneHidden(true);
              }}
            />
          </div>

          {this.renderPaginationControls()}
        </div>

        {this.renderSearchResultsTable()}
      </div>
    );
  }
}

export function RegistryEditorPage(props: { path: string }) {
  return (
    <PageContent style={{ height: '100%' }}>
      <SplitGridWithHeader>
        <PageContentHeader>Registry Editor</PageContentHeader>
        <RegistryEditorComp />
        <SearchPane />
      </SplitGridWithHeader>
    </PageContent>
  );
}
